import { encodePayload, hexToBytes } from './codec.js';
import { HMAC_BYTES } from './constants.js';

/**
 * Subtle Crypto está disponible en Node 20+, navegadores modernos y RN >= 0.72
 * con el polyfill `react-native-get-random-values` + `expo-crypto`.
 */
function getSubtle(): SubtleCrypto {
  const subtle = (globalThis.crypto as Crypto | undefined)?.subtle;
  if (!subtle) {
    throw new Error('SubtleCrypto unavailable. Install a polyfill in this runtime.');
  }
  return subtle;
}

/**
 * HMAC-SHA256(secret, qrId) → primeros HMAC_BYTES bytes.
 *
 * El QR de Boletra es estático: la firma se calcula sobre el qrId solo, sin
 * ventana temporal. Esto permite que el mismo string viva en Apple Wallet,
 * Google Wallet y en la PWA — todos muestran un QR idéntico, y el scanner
 * lo valida igual en cualquier momento. La defensa anti-screenshot la da
 * el `scanCount` server-side (el primer scan marca el QR como validated;
 * el segundo se reporta como duplicate al operador).
 */
export async function computeHmac(secret: Uint8Array, qrIdBytes: Uint8Array): Promise<Uint8Array> {
  const subtle = getSubtle();
  const key = await subtle.importKey(
    'raw',
    secret as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await subtle.sign('HMAC', key, qrIdBytes as BufferSource);
  return new Uint8Array(sig).slice(0, HMAC_BYTES);
}

export interface QrCode {
  payload: string;
}

/**
 * Genera el código estático para un QR. La PWA y los pases de wallet llaman
 * esto una vez y muestran el mismo payload por siempre — ya no hay refresh
 * cada 30s.
 */
export async function generateCode(qrIdHex: string, secretBase64: string): Promise<QrCode> {
  const qrIdBytes = hexToBytes(qrIdHex);
  const secret = base64ToBytes(secretBase64);
  const hmacBytes = await computeHmac(secret, qrIdBytes);
  const payload = encodePayload({ qrIdBytes, hmacBytes });
  return { payload };
}

function base64ToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  if (typeof atob === 'function') {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  if (globalThis.Buffer) return new Uint8Array(globalThis.Buffer.from(b64, 'base64'));
  throw new Error('No base64 decoder available');
}
